import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import BaseModalComponent from './BaseModal.component';
import { useAppDispatch } from '../../hooks/useAppDispatcher.hook';
import InputComponent from '../Input/Input.component';
import IconButtonComponent from '../IconButton.component';
import FiltersIcon from '../Icons/Filters.icon';
import { getCandidateSearchThunk } from '../../redux/Candidate/Candidate.thunk';
import { PersonList } from '../../redux/Candidate/Candidate.type';
import NoResultComponent from '../NoResult.component';
import CardComponent, { StyledCard } from '../Card.component';
import { uniqueId, debounce } from 'lodash';
import { RoutesConstant } from '../../constants/routes.constant';
import TableRowComponent from '../Table/TableRow.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { DATE_TIME_FORMAT } from '../../constants/date.contant';
import NotAssignComponent from '../NotAssign.component';
import BadgeComponent from '../Badge.component';
import { useModalManager } from '../../hooks/ModalManager.hook';
import { getUrlBasedOnCurrentPath } from './Candidate/utils';
import { CANDIDATES_GROUPS } from '../../types/candidates.types';
import { dateFormatter } from '../../utils/formatters';
import { StyledGlobalSearch } from '../../styles';
import SpinnerComponent from '../Spinner.component';
import { StatusColor } from '../../constants/statusColors.constant';
import CandidatePreviewModalComponent from './Candidate/CandidatePreviewModal.component';
import InfiniteScroll from 'react-infinite-scroll-component';

const StyledStylesRoot = css`
  max-height: 600px;
  min-height: 600px;
  max-width: 860px;
  min-width: 860px;
`;

const StyledEmptyResult = styled(StyledCard)`
  display: flex;
  align-items: center;
  justify-content: center;
  ${StyledStylesRoot}
`;

const StyledResultCardComponent = styled(StyledCard)`
  overflow: auto;
  ${StyledStylesRoot}
`;

const StyledGlobalSearchWrapper = styled.div`
  display: grid;
  grid-template-columns: auto 32px;
  align-items: center;
  gap: 10px;
`;

const DEBOUNCE_TIME = 300;

const GlobalSearchModal = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { openModal, closeModal } = useModalManager();
  const navigate = useNavigate();
  const location = useLocation();
  const [firstSearch, setFirstSearch] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [list, setList] = useState<PersonList[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const onSearch = async (search: string, page: number = 1): Promise<void> => {
    try {
      const result = await dispatch(
        getCandidateSearchThunk({
          search,
          pageSize: 15,
          page,
        }),
      );
      if (!result.payload) return;
      setList((data) => page === 1 ? result.payload.data : [...data, ...result.payload.data]);
      setPage(result.payload.pagination.current_page);
      setPages(result.payload.pagination.last_page);
    } finally {
      setLoading(false);
      setFirstSearch(false);
    }
  };

  useEffect(() => {
    onSearch('', 1);
  }, []);

  const searchDebounce = useCallback(debounce(onSearch, DEBOUNCE_TIME), []);

  const handleInputChange = (searchValue: string) => {
    setSearch(searchValue);
    searchDebounce(searchValue);
  };

  const prepareRowColumns = (item: PersonList) => {
    const date = item.date ? dateFormatter(item.date, DATE_TIME_FORMAT) : '-';

    return [
      date,
      item.fullName,
      <NotAssignComponent mode="badge" has={!!item.job}>
        {item.job}
      </NotAssignComponent>,
      <NotAssignComponent mode="badge" has={!!item.experience}>
        <BadgeComponent
          appearance="default"
          text={item.experience ? item.experience.name : ''}
        />
      </NotAssignComponent>,
      <NotAssignComponent mode="badge" has={!!item.status}>
        <BadgeComponent
          appearance="default"
          color={
            item.statusColor && i18n.exists(`statusColor.${item.statusColor}`)
              ? `STATUSES_${item.statusColor.toUpperCase()}`
              : item.statusColor || `STATUSES_${StatusColor.GREY.toUpperCase()}`
          }
          textColor={
            item.statusTextColor &&
            i18n.exists(`statusColor.${item.statusTextColor}`)
              ? `STATUSES_${item.statusTextColor.toUpperCase()}`
              : item.statusTextColor
          }
          text={item.status!}
        />
      </NotAssignComponent>,
    ];
  };

  const handleCandidateClick = (id: number | string) => {
    openModal(
      CandidatePreviewModalComponent,
      {
        personId: id,
        candidateGroup: CANDIDATES_GROUPS.ALL,
        closeUpperModal: closeModal,
      },
      {
        position: 'right-top',
      },
    );
  };

  const renderSearchList = useMemo(() => {
    return list.map((item: PersonList) => {
      const personUrl = getUrlBasedOnCurrentPath(
        location,
        item.id,
        [RoutesConstant.CANDIDATES_GROUP, RoutesConstant.CANDIDATE],
        CANDIDATES_GROUPS.ALL,
      );
      return (
        <TableRowComponent
          key={uniqueId('table-rows-')}
          columns={prepareRowColumns(item)}
          handleClick={() => handleCandidateClick(item.id)}
          handleDBClick={() => {
            closeModal();
            !!personUrl && navigate(personUrl);
          }}
        />
      );
    });
  }, [list, location]);

  const renderBlockSearchList = useMemo(() => {
    if (firstSearch) {
      return (
        <StyledResultCardComponent>
          <SpinnerComponent size="large" />
        </StyledResultCardComponent>
      );
    }

    if (!list.length)
      return (
        <CardComponent>
          <StyledEmptyResult>
            <NoResultComponent />
          </StyledEmptyResult>
        </CardComponent>
      );

    return (
      <StyledResultCardComponent id="scrollableDiv">
        <InfiniteScroll
          dataLength={list.length}
          next={() => onSearch(search, page + 1)}
          hasMore={page < pages}
          loader={
            <div>
              <SpinnerComponent size="medium" />
            </div>
          }
          scrollableTarget="scrollableDiv"
        >
          {renderSearchList}
        </InfiniteScroll>
      </StyledResultCardComponent>
    );
  }, [firstSearch, list, renderSearchList]);

  return (
    <StyledGlobalSearch>
      <BaseModalComponent hasHeader={false} rightBlock={renderBlockSearchList}>
        <StyledGlobalSearchWrapper>
          <InputComponent
            autoFocus
            disabled={isLoading}
            placeholder={t('Search name, surname, phone, email')}
            value={search}
            onChange={handleInputChange}
            mode="transparent"
          />
          <IconButtonComponent
            disabled={isLoading}
            onClick={() => {
              closeModal();
              navigate(
                `${RoutesConstant.CANDIDATES_GROUPS.SEARCH}?keywords=${search}`,
              );
            }}
          >
            <FiltersIcon />
          </IconButtonComponent>
        </StyledGlobalSearchWrapper>
      </BaseModalComponent>
    </StyledGlobalSearch>
  );
};

export default GlobalSearchModal;
